<template>
    <section
        v-if="isAvailable"
        :id="id"
        class="s-resource-highlight"
        :class="{ ...className, ...basicClassName }"
    >
        <div class="a-container">
            <div v-if="hasHeader" class="s-resource-highlight__header">
                <div v-if="label" class="s-resource-highlight__header__label">
                    {{ label }}
                </div>
                <div class="s-resource-highlight__header__content">
                    <div
                        v-if="title"
                        class="s-resource-highlight__header__content__title"
                    >
                        {{ title }}
                    </div>
                    <div
                        v-if="lead"
                        class="s-resource-highlight__header__content__lead"
                        v-html="lead"
                    />
                </div>
                <div v-if="link" class="s-resource-highlight__header__link">
                    <a-link
                        v-bind="link"
                        :size="link.size || 'title'"
                        :glyph="link.glyph || 'arrow'"
                        accent
                    />
                </div>
            </div>
            <div v-if="isCardSingle" class="s-resource-highlight__content">
                <a-resource-highlight-card
                    class="s-resource-highlight__card single"
                    v-bind="resolvedCards[0]"
                    :is-single="true"
                    :hide-link="localCards.length"
                    :compact-mobile="compactMobile"
                    :title-tag="$attrs.title && 'h3'"
                />
            </div>
            <div v-else class="s-resource-highlight__content">
                <div v-if="isCardsList" class="s-resource-highlight__list">
                    <a-resource-highlight-card
                        v-for="(card, index) in resolvedCards"
                        v-show="!visibleCards || visibleCards > index"
                        :key="`list-item-${index}`"
                        :hide-link="localCards.length"
                        class="s-resource-highlight__card"
                        v-bind="card"
                    />
                </div>
                <a-slider
                    v-else
                    class="s-resource-highlight__slider"
                    :options="sliceSliderOptions"
                >
                    <a-slide
                        v-for="(card, index) in sliceCards"
                        :key="`innovation-leadership-slide-${index}`"
                        class="s-resource-highlight__slide"
                    >
                        <a-resource-highlight-card
                            class="s-resource-highlight__card"
                            v-bind="card"
                            :hide-link="localCards.length"
                            :compact-mobile="compactMobile"
                            :title-tag="$attrs.title && 'h3'"
                        />
                    </a-slide>
                </a-slider>
            </div>
            <template v-if="settings && step > 0">
                <div
                    v-if="settings.more && isAvailable"
                    class="s-resource-highlight__show-more"
                >
                    <a-link
                        :text="settings.more"
                        size="title"
                        glyph="drop-down-arrow"
                        accent
                        @click.stop.prevent="handleClick"
                    />
                </div>
                <div
                    v-if="settings.less && !isAvailable"
                    class="s-resource-highlight__show-less"
                >
                    <a-link
                        :text="settings.less"
                        size="title"
                        glyph="drop-down-arrow"
                        accent
                        @click.stop.prevent="handleClick"
                    />
                </div>
            </template>
            <div v-if="link" class="s-resource-highlight__mobile__link">
                <a-link
                    v-bind="link"
                    :size="link.size || 'title'"
                    :glyph="link.glyph || 'arrow'"
                    accent
                />
            </div>
        </div>
    </section>
</template>

<script>
import { mapState } from 'vuex';
import { ProductAPIQueryBuilder } from '@api/builders/product';
import ALink from '@core/components/link/link.vue';
import AResourceHighlightCard from '@core/components/resource-highlight-card/resource-highlight-card.vue';
import ASlide from '@core/components/slider/slide.vue';
import ASlider from '@core/components/slider/slider.vue';
import contentMixin from '@core/mixins/content.js';
import styleMixin from '@core/mixins/style.js';
import { LOCALE_DEFAULT } from '@model/const/locales.ts';
import { RC_ROOT_URL } from '@model/const/resource-center';

export default {
    name: 'SResourceHighlight',

    components: {
        ALink,
        ASlider,
        ASlide,
        AResourceHighlightCard,
    },
    mixins: [contentMixin, styleMixin],
    inheritAttrs: false,

    props: {
        compactMobile: {
            type: Boolean,
            required: false,
            default: true,
        },
        label: {
            type: String,
            required: false,
            default: undefined,
        },
        cards: {
            type: Array,
            required: true,
        },
        link: {
            type: Object,
            required: false,
            default: undefined,
        },
        isCardsList: {
            type: Boolean,
            default: false,
        },
        sliderOptions: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        settings: {
            type: Object,
            required: false,
            default: undefined,
        },
    },

    data() {
        return {
            step: 0,
            visibleCards: 0,
            visibleTags: 2,
            localCards: [],
            sliceSliderOptions: {
                slidesPerView: 'auto',
                showNavigation: true,
                // disabled due to Swiper conflict between the Fraction pagination and slidesPerView: auto
                showCounter: false,
                ...this.sliderOptions,
            },
        };
    },
    computed: {
        ...mapState({
            productID: (state) => state.pages?.page?.product_id || null,
            pageAudience: (state) => state.pages?.page?.audience_id || null,
            pageAudiences: (state) => state.pages?.audiences?.items || [],
        }),

        audienceName() {
            const match = this.pageAudiences.find(a => a.id === this.pageAudience);
            return match?.name || null;
        },

        mappedAudienceIds() {
            const mapping = {
                Cloud: [2],
                Consumer: [8],
                Corporate: [1],
                Generic: [1],
                Split: [1],
            };

            return mapping[this.audienceName] || [1];
        },

        locale() {
            return this.$store.state.geolocation.finalLocale || LOCALE_DEFAULT;
        },
        resolvedCards() {
            return this.cards?.length ? this.cards : this.localCards;
        },
        sliceCards() {
            return this.resolvedCards?.slice(0, this.settings?.limit || 6);
        },
        isCardSingle() {
            return this.resolvedCards?.length === 1;
        },
        className() {
            return {
                's-resource-highlight_single-card': this.isCardSingle,
                's-resource-highlight_cards-list': this.isCardsList,
            };
        },
        hasHeader() {
            return this.label || this.title || this.lead || this.link;
        },
        isAvailable() {
            return this.resolvedCards.length && this.visibleCards <= this.resolvedCards.length;
        },
    },

    mounted() {
        if (this.settings && this.settings.visibleCards) {
            this.visibleCards = this.settings.visibleCards;

            if (this.resolvedCards.length > this.visibleCards) {
                this.step = this.settings.step || this.resolvedCards.length;
            }
        }
        this.visibleTags = this.settings?.visibleTags
            ? this.settings?.visibleTags
            : 2;
    },

    async created() {
        if (!this.cards?.length) {
            await this.fetchEntityData();
        }
    },

    methods: {
        extendResourceData(resource) {
            return {
                link: {
                    to: `${RC_ROOT_URL}/resource/${resource.slug}/`,
                    text: 'Read case study',
                    glyph: 'arrow',
                },
                title: resource.seo_title,
                text: resource.seo_description,
                category: resource.type.title,
                image: resource.cover_image_id,
                imageAlt: resource.seo_title,
                tags: (resource.tags || []).map(el => ({ text: el.title })).slice(0, this.visibleTags),
            };
        },
        handleClick() {
            if (this.isAvailable) {
                this.visibleCards += this.step;
                return;
            }
            this.visibleCards = this.settings.visibleCards;
        },
        async fetchEntityData() {
            const hasFilters = {
                products: this.settings?.filters?.products || [this.productID],
                audiences: this.settings?.filters?.audiences || this.mappedAudienceIds,
            };

            if (this.settings?.filters?.topics) {
                hasFilters.tags = this.settings.filters.topics;
            }


            const request = new ProductAPIQueryBuilder('resourceHighlight')
                .setEntityPath('/api/resources/resources/')
                .setCustomParam('has', hasFilters)
                .addMatchesAll('type_id', 'in', this.settings?.filters?.types || [5])
                .addSort('translation.published_at', 'desc')
                .addSort('id', 'desc')
                .setLocales([this.locale])
                .setCustomParam('process-macros', '1')
                .toObject();

            try {
                await this.$store.dispatch('resourceCenter/getEntity', { request });
                const rawItems = this.$store.state.resourceCenter?.resourceHighlight?.items || [];
                this.localCards = rawItems.map(item => this.extendResourceData(item, this.locale));
            } catch (e) {
                // Empty to avoid crashes
            }
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-resource-highlight {
    @mixin basic-slice-paddings;
    overflow-x: hidden;

    &__header {
        margin-bottom: 40px;
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;

        &__label {
            @mixin paragraph;
            color: var(--av-fixed-light);
            flex-basis: 100%;
            margin-bottom: 8px;

            @media (--viewport-desktop) {
                @mixin colls 8;
            }
        }

        &__content {
            @media (--viewport-desktop) {
                @mixin colls 8;
            }

            &__title {
                @mixin display;
                margin-bottom: 24px;
                color: var(--av-nav-primary);

                @media (--viewport-mobile-wide) {
                    @mixin hero;
                }
            }

            &__lead {
                @mixin lead;
                color: var(--av-nav-primary);

                @media (--viewport-mobile-wide) {
                    @mixin title;
                }
            }
        }

        &__link {
            text-align: end;

            .a-link {
                margin-top: 8px;
            }

            display: none;

            @media (--viewport-desktop) {
                display: block;
                margin-inline-start: auto;
                @mixin colls 4;
            }

            @media (--viewport-desktop-wide) {
                @mixin colls 3;
            }
        }
    }

    &_cards-list,
    &_single-card {
        overflow-x: visible;

        .s-resource-highlight__content {
            margin: 0;
        }
    }

    &__list {
        .a-resource-highlight-card {
            &:not(:first-child) {
                margin-top: 16px;
            }
        }
    }

    &__content {
        &:deep(.a-slider__nav) {
            margin-top: 48px;
            margin-bottom: 0;
        }
    }

    &__card.single {
        @mixin colls 12;

        @media (--viewport-tablet) {
            @mixin colls 10;
        }
    }

    &__card {
        height: 100%;
    }

    &:deep(.a-slider) {
        overflow: hidden;

        @media (--viewport-tablet) {
            overflow: visible;
        }

        @media (--viewport-desktop-large) {
            overflow: hidden;

            /*
            .a-slide_next {
                .s-resource-highlight__card {
                    mask-image: linear-gradient(90deg, rgba(0, 0, 0, 0.6), transparent);
                }
            }
            */
        }

        .a-slide {
            @mixin colls 12;

            @media (--viewport-tablet) {
                @mixin colls 10;
                margin-inline-end: 16px;
            }

            &:last-child {
                @mixin colls 12;
                margin-inline-end: 0;

                .s-resource-highlight__card {
                    @mixin colls 12;

                    @media (--viewport-tablet) {
                        @mixin colls 10;
                    }
                }
            }

            &_next {
                opacity: 0.5;
                position: relative;
            }

            &.swiper-slide-prev {
                opacity: 0;
            }
        }

        &__counter {
            &-total,
            &-text,
            &-current {
                @mixin lead;
                color: var(--av-fixed-light);
            }
        }

        &_numbers_off {
            .a-slider__dots {
                display: none;
            }
        }
    }

    &:deep(.a-slider__nav) {
        margin-inline-start: 8px;
    }

    &__mobile__link {
        @media (--viewport-desktop) {
            display: none;
        }
    }

    &__show-more,
    &__show-less,
    &__mobile__link {
        .a-link {
            cursor: pointer;
            margin-top: 48px;

            &:focus {
                background: none;
            }
        }
    }

    &__show-less {
        &:deep(.a-glyph) {
            transform: rotate(180deg);
        }
    }
}

.s-side-menu {
    .a-container {
        .s-resource-highlight {
            padding-bottom: 0;
            padding-top: 56px;

            @media (--viewport-desktop) {
                &:not(&:first-child) {
                    padding: 0;
                    margin: 104px 0 0;
                }
            }

            &:deep(.a-slice-header) {
                margin: 0;

                @media (--viewport-desktop) {
                    margin: 0 0 40px;
                }

                &__title {
                    display: none;

                    @media (--viewport-desktop) {
                        display: block;
                    }
                }
            }
        }

        .a-resource-highlight-card {
            &__footer {
                display: block;
            }

            &__company {
                margin-bottom: 24px;
            }

            @media (--viewport-desktop-large) {
                &__footer {
                    display: flex;
                }

                &__company {
                    margin-bottom: 0;
                }
            }
        }
    }
}
</style>
